import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function ConfigureDevice() {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    modify_interval: '',
    modify_apn: '',
    firmware_url: '',
    firmware_version: '',
    pub_topic: '',
    sub_topic: '',
    ack_topic: '',
    mqtt_host: '',
    mqtt_port: '',
    mqtt_user: '',
    mqtt_pass: '',
    factory_reset: false,
    get_settings: false
  });

  const [firmwareFiles, setFirmwareFiles] = useState([]);
  const [deviceSettings, setDeviceSettings] = useState(null);

  useEffect(() => {
    fetchFirmwareFiles();
    fetchDeviceSettings();

    const intervalId = setInterval(() => {
      fetchDeviceSettings();
    }, 1000); // fetch every 1 second

    return () => clearInterval(intervalId); // cleanup on unmount
  }, []);

  const fetchFirmwareFiles = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/superadmin/firmware-files`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch files');
      const data = await res.json();
      setFirmwareFiles(data.files || []);
    } catch (err) {
      console.error(err);
      alert('Failed to load firmware files');
    }
  };

  const fetchDeviceSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BASE_URL}/superadmin/devices/settings/${deviceId}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) throw new Error('Failed to fetch settings');
      const data = await res.json();
      setDeviceSettings(data);
    } catch (err) {
      console.error(err);
    }
  };

  const generateRequestId = () => {
    const t = new Date().toISOString().replace(/[-:.TZ]/g, '').slice(0, 14);
    return `${t}-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const commands = [];

    if (form.modify_interval) {
      commands.push({ command: 'modify_interval', value: Number(form.modify_interval) });
    }

    if (form.modify_apn) {
      commands.push({ command: 'modify_apn', value: form.modify_apn });
    }

    if (form.pub_topic || form.sub_topic || form.ack_topic) {
      commands.push({
        command: 'modify_topics',
        value: {
          pub: form.pub_topic,
          sub: form.sub_topic,
          ack: form.ack_topic
        }
      });
    }

    if (form.mqtt_host || form.mqtt_port || form.mqtt_user || form.mqtt_pass) {
      commands.push({
        command: 'modify_mqtt',
        value: {
          host: form.mqtt_host,
          port: Number(form.mqtt_port),
          username: form.mqtt_user,
          password: form.mqtt_pass
        }
      });
    }

    if (form.factory_reset) {
      commands.push({ command: 'factory_reset', value: null });
    }

    if (form.get_settings) {
      commands.push({ command: 'get_settings' });
    }

    if (form.firmware_url && form.firmware_version) {
      commands.push({
        command: 'firmware_update',
        device_id: deviceId,
        version: form.firmware_version,
        value: form.firmware_url
      });
    }

    const request_id = generateRequestId();

    let payload;

    if (commands.length === 1 && commands[0].command !== 'get_settings') {
      const cmd = commands[0];
      payload = {
        request_id,
        command: cmd.command,
        value: cmd.value
      };

      if (cmd.command === 'firmware_update') {
        payload.device_id = deviceId;
        payload.version = form.firmware_version;
      }
    } else {
      payload = {
        request_id,
        operations: commands
      };
    }

    try {
      await API.post(`/superadmin/devices/${deviceId}/send-commands`, payload);
      alert('✅ Command(s) sent successfully');
    } catch (err) {
      console.error(err);
      alert('❌ Failed to send command');
    }
  };

  return (
    <div style={{ display: 'flex', padding: 20 }}>
      {/* LEFT SIDE: CONFIGURATION FORM */}
      <div style={{ flex: 1, marginRight: 40 }}>
        <h2>Configure Device: {deviceId}</h2>
        <form onSubmit={handleSubmit}>
          <label>Modify Interval (sec):</label><br />
          <input name="modify_interval" value={form.modify_interval} onChange={handleChange} /><br /><br />

          <label>Modify APN:</label><br />
          <input name="modify_apn" value={form.modify_apn} onChange={handleChange} /><br /><br />

          <label>Modify Topics:</label><br />
          <input name="pub_topic" placeholder="Publish" value={form.pub_topic} onChange={handleChange} /><br />
          <input name="sub_topic" placeholder="Subscribe" value={form.sub_topic} onChange={handleChange} /><br />
          <input name="ack_topic" placeholder="Ack" value={form.ack_topic} onChange={handleChange} /><br /><br />

          <label>Modify MQTT Broker:</label><br />
          <input name="mqtt_host" placeholder="Host" value={form.mqtt_host} onChange={handleChange} /><br />
          <input name="mqtt_port" placeholder="Port" value={form.mqtt_port} onChange={handleChange} /><br />
          <input name="mqtt_user" placeholder="Username" value={form.mqtt_user} onChange={handleChange} /><br />
          <input name="mqtt_pass" placeholder="Password" value={form.mqtt_pass} onChange={handleChange} /><br /><br />

          <label>Firmware Update:</label><br />
          <input name="firmware_version" placeholder="Version" value={form.firmware_version} onChange={handleChange} /><br />
          <select name="firmware_url" value={form.firmware_url} onChange={handleChange}>
            <option value="">-- Select Firmware File --</option>
            {firmwareFiles.map(file => (
              <option key={file.url} value={file.url}>{file.filename}</option>
            ))}
          </select><br /><br />

          <label>
            <input type="checkbox" name="factory_reset" checked={form.factory_reset} onChange={handleChange} />
            Factory Reset
          </label><br />

          <label>
            <input type="checkbox" name="get_settings" checked={form.get_settings} onChange={handleChange} />
            Get Settings
          </label><br /><br />

          <button type="submit">Send</button>
        </form>
        <button onClick={() => navigate(-1)} style={{ marginTop: 20 }}>Back</button>
      </div>

      {/* RIGHT SIDE: DEVICE SETTINGS DISPLAY */}
      <div style={{ flex: 1 }}>
        <h3>Device Settings</h3>
        {deviceSettings ? (
          <pre style={{ background: '#f5f5f5', padding: 10, borderRadius: 6 }}>
            {JSON.stringify(deviceSettings, null, 2)}
          </pre>
        ) : (
          <p>Loading settings...</p>
        )}
      </div>
    </div>
  );
}
